// Misc
import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import padStart from 'lodash/padStart'
import pick from 'lodash/pick'
import first from 'lodash/first'
import some from 'lodash/some'
import size from 'lodash/size'
import map from 'lodash/map'
import keys from 'lodash/keys'
import filter from 'lodash/filter'
import moment from 'moment'
import { withStyles } from '@material-ui/core/styles'

// Utils
import { momentOffset } from 'utils/time'

// Validation
import { Validator } from 'react-vld'
import { validateRequiredDate, validateMinDate } from 'utils/validation'

// Atoms
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Input, { InputPropTypes } from 'atoms/Input'
import Popover from 'atoms/Popover'

// Icons
import CloseIcon from '@material-ui/icons/Close'

// Styles
const styles = theme => ({
  root: {
    display: 'flex',
  },
  rootWithErrorMessage: {
    '& $buttonLabel': {
      color: theme.palette.error.main
    }
  },
  rootDisabled: {
    '& $button': {
      cursor: 'default',
      opacity: 0.5,
    }
  },
  button: {
    border: 0,
    padding: 0,
    margin: 0,
    outline: 0,
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: theme.transitions.create(['opacity']),
  },
  buttonLabel: {
    height: '2rem',
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center',
  },
  deleteButton: {
    width: '2rem',
    height: '2rem',
  },
  deleteButtonIcon: {
    width: theme.spacing.unit * 2,
    height: theme.spacing.unit * 2,
  },
  popover: {
    display: 'flex',
    maxHeight: theme.spacing.unit * 20,
  },
  popoverCol: {
    overflowY: 'auto',
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.grey['200']}`
    }
  },
  popoverVal: {
    outline: 0,
    cursor: 'pointer',
    textAlign: 'center',
    minWidth: theme.spacing.unit * 8,
    lineHeight: theme.typography.subtitle2.lineHeight,
    transition: theme.transitions.create(['opacity', 'background-color']),
  },
  popoverValSelected: {
    fontWeight: theme.typography.subtitle2.fontWeight,
    backgroundColor: theme.palette.grey['100'],
  },
  popoverValDisabled: {
    cursor: 'default',
    opacity: 0.5,
  },
})

const format = 'h:mm a'

class TimePickerContent extends PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    date: PropTypes.instanceOf(Date),
    utcOffset: PropTypes.number,
    availability: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    onMeridiemClick: PropTypes.func.isRequired,
  }

  constructor({ utcOffset, availability }) {
    super()
    const dayStart = momentOffset(utcOffset).startOf('day')
    this.availableTimeRanges = map(availability, ({ startTimeMinute, endTimeMinute }) => ({
      startTime: dayStart.clone().add(startTimeMinute, 'minutes'),
      endTime: dayStart.clone().add(endTimeMinute, 'minutes'),
    }))
  }

  componentDidMount() {
    if (this.selectedHourNode) this.selectedHourNode.scrollIntoView()
    if (this.selectedMinuteNode) this.selectedMinuteNode.scrollIntoView()
  }

  getDefaultDateMoment() {
    const { utcOffset, date } = this.props
    return date
      ? momentOffset(utcOffset, date)
      : momentOffset(utcOffset).startOf('day')
  }

  isTimeRangeContainsHour = ({ startTime, endTime }, hour) => {
    return hour >= startTime.hour() && hour <= endTime.hour()
  }

  isTimeRangeContainsMeridiem = ({ startTime, endTime }, hour, meridiem) => {
    const hourAm = hour % 12
    const hourPm = hourAm + 12
    return (this.isTimeRangeContainsHour({ startTime, endTime }, hourAm) && meridiem === 'am')
      || (this.isTimeRangeContainsHour({ startTime, endTime }, hourPm) && meridiem === 'pm')
  }

  isTimeRangeContainsMinute = ({ startTime, endTime }, hour, minute) => {
    const dateMoment = startTime.clone().hour(hour).minute(minute)
    return !dateMoment.isBefore(startTime) && !dateMoment.isAfter(endTime)
  }

  getHourTimeRanges = (hour) => {
    return filter(this.availableTimeRanges, timeRange => (
      this.isTimeRangeContainsHour(timeRange, hour)
    ))
  }

  isAvailableHour = (hour) => {
    return some(this.availableTimeRanges, timeRange => (
      this.isTimeRangeContainsHour(timeRange, hour)
    ))
  }

  isAvailableMeridiem = (hour, meridiem) => {
    return some(this.availableTimeRanges, timeRange => (
      this.isTimeRangeContainsMeridiem(timeRange, hour, meridiem)
    ))
  }

  isAvailableMinute = (hour, minute) => {
    return some(this.availableTimeRanges, timeRange => (
      this.isTimeRangeContainsMinute(timeRange, hour, minute)
    ))
  }

  isHourEnabled = (hour) => {
    if (!size(this.availableTimeRanges)) return true
    return this.isAvailableHour(hour)
  }

  isMeridiemEnabled = (meridiem) => {
    const { utcOffset, date } = this.props

    if (!size(this.availableTimeRanges)) return true
    if (!date) return false

    const dateMoment = momentOffset(utcOffset, date)
    const hour = dateMoment.hour()

    return this.isAvailableMeridiem(hour, meridiem)
  }

  isMinuteEnabled = (minute) => {
    const { utcOffset, date } = this.props

    if (!size(this.availableTimeRanges)) return true
    if (!date) return false

    const dateMoment = momentOffset(utcOffset, date)
    const hour = dateMoment.hour()

    return this.isAvailableMinute(hour, minute)
  }

  triggerChange = (dateMoment) => {
    const { onChange, availability } = this.props

    if (!size(availability)) {
      onChange(dateMoment.toDate())
      return
    }

    let hour = dateMoment.hour()
    let minute = dateMoment.minute()

    if (!this.isAvailableHour(hour)) {
      if (hour <= 12 && this.isAvailableHour(hour + 12)) {
        hour += 12
      } else if (hour > 12 && this.isAvailableHour(hour - 12)) {
        hour -= 12
      } else {
        hour = first(this.availableTimeRanges).startTime.hour()
      }
    }

    if (!this.isAvailableMinute(hour, minute)) {
      minute = first(this.getHourTimeRanges(hour)).startTime.minute()
    }

    dateMoment.hour(hour).minute(minute)
    onChange(dateMoment.toDate())
  }

  handleHourClick = value => () => {
    const dateMoment = this.getDefaultDateMoment()
    const hour = dateMoment.hour()
    dateMoment.hour(hour >= 12 ? value + 12 : value)
    this.triggerChange(dateMoment)
  }

  handleMinuteClick = value => () => {
    const dateMoment = this.getDefaultDateMoment()
    dateMoment.minute(value)
    this.triggerChange(dateMoment)
  }

  handleMeridiemClick = value => () => {
    const { onMeridiemClick } = this.props
    const dateMoment = this.getDefaultDateMoment()
    const hour = dateMoment.hour()
    dateMoment.hour(value === 'am' ? hour % 12 : (hour % 12) + 12)
    this.triggerChange(dateMoment)
    onMeridiemClick()
  }

  selectedHourRef = (node) => {
    this.selectedHourNode = node
  }

  selectedMinuteRef = (node) => {
    this.selectedMinuteNode = node
  }

  renderHours() {
    const { classes, utcOffset, date } = this.props
    const hour = date && momentOffset(utcOffset, date).format('hh')
    const children = []
    for (let i = 0; i < 12; i += 1) {
      const label = i === 0 ? '12' : padStart(i, 2, '0')
      const selected = label === hour
      const disabled = !this.isHourEnabled(i) && !this.isHourEnabled(i + 12)
      const cx = classNames(classes.popoverVal, {
        [classes.popoverValSelected]: selected,
        [classes.popoverValDisabled]: disabled,
      })
      children.push(
        <div key={i} ref={selected && this.selectedHourRef}>
          <Typography // eslint-disable-line
            className={cx}
            tabIndex="-1"
            color="inherit"
            onClick={disabled ? undefined : this.handleHourClick(i)}
          >
            { label }
          </Typography>
        </div>
      )
    }
    return (
      <div className={classes.popoverCol}>
        { children }
      </div>
    )
  }

  renderMinutes() {
    const { classes, utcOffset, date } = this.props
    const minute = date && momentOffset(utcOffset, date).format('mm')
    const children = []
    for (let i = 0; i < 60; i += 1) {
      const label = padStart(i, 2, '0')
      const selected = label === minute
      const disabled = !this.isMinuteEnabled(i)
      const cx = classNames(classes.popoverVal, {
        [classes.popoverValSelected]: selected,
        [classes.popoverValDisabled]: disabled,
      })
      children.push(
        <div key={i} ref={selected && this.selectedMinuteRef}>
          <Typography // eslint-disable-line
            className={cx}
            tabIndex="-1"
            color="inherit"
            onClick={disabled ? undefined : this.handleMinuteClick(i)}
          >
            { label }
          </Typography>
        </div>
      )
    }
    return (
      <div className={classes.popoverCol}>
        { children }
      </div>
    )
  }

  renderMeridiem() {
    const { classes, utcOffset, date } = this.props
    const meridiem = date && momentOffset(utcOffset, date).format('a')
    const children = ['am', 'pm'].map((value) => {
      const selected = value === meridiem
      const disabled = !this.isMeridiemEnabled(value)
      const cx = classNames(classes.popoverVal, {
        [classes.popoverValSelected]: selected,
        [classes.popoverValDisabled]: disabled,
      })
      return (
        <Typography // eslint-disable-line
          key={value}
          className={cx}
          tabIndex="-1"
          color="inherit"
          onClick={disabled ? undefined : this.handleMeridiemClick(value)}
        >
          { value }
        </Typography>
      )
    })
    return (
      <div className={classes.popoverCol}>
        { children }
      </div>
    )
  }

  render() {
    const { classes } = this.props
    return (
      <div className={classes.popover}>
        { this.renderHours() }
        { this.renderMinutes() }
        { this.renderMeridiem() }
      </div>
    )
  }
}

@withStyles(styles) // eslint-disable-line
class TimePicker extends PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    validation: PropTypes.object,
    date: PropTypes.instanceOf(Date),
    defaultDate: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    utcOffset: PropTypes.number,
    disabled: PropTypes.bool,
    availability: PropTypes.arrayOf(PropTypes.shape({
      startTimeMinute: PropTypes.number.isRequired,
      endTimeMinute: PropTypes.number,
    })),
  }

  static defaultProps = {
    onChange: () => {},
    placeholder: 'Enter time',
  }

  constructor({ date, defaultDate }) {
    super()
    this.state = { date: date || defaultDate, anchorEl: null }
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    const { date } = nextProps
    if (
      date !== undefined
      && !moment(date).isSame(prevState.date)
      && !prevState.anchorEl
    ) {
      return { date }
    }
    return null
  }

  invokeChange = () => {
    const { date } = this.state
    const { onChange, validation } = this.props
    if (validation) validation.reset({ updateComponent: false })
    this.setState({ anchorEl: null }, () => onChange(date))
  }

  handleButtonClick = (ev) => {
    const { disabled } = this.props
    if (!disabled) this.setState({ anchorEl: ev.currentTarget })
  }

  handlePopoverClose = () => {
    this.invokeChange()
  }

  handleDelete = () => {
    this.setState({ date: null }, this.invokeChange)
  }

  handleDateChange = (date) => {
    this.setState({ date })
  }

  handleMeridiemChange = () => {
    const { availability } = this.props
    if (!size(availability)) {
      setTimeout(this.invokeChange)
    }
  }

  renderPopoverContent() {
    const { classes, availability, utcOffset } = this.props
    const { date } = this.state
    return (
      <TimePickerContent
        date={date}
        classes={classes}
        utcOffset={utcOffset}
        availability={availability}
        onChange={this.handleDateChange}
        onMeridiemClick={this.handleMeridiemChange}
      />
    )
  }

  render() {
    const InputProps = pick(this.props, keys(InputPropTypes))
    const { classes, placeholder, required, validation, utcOffset, disabled } = this.props
    const { anchorEl, date } = this.state
    const errorMessage = validation && validation.isInvalid ? validation.error.message : null
    const cx = classNames(classes.root, {
      [classes.rootWithErrorMessage]: !!errorMessage,
      [classes.rootDisabled]: disabled
    })
    return (
      <Fragment>
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={this.handlePopoverClose}
        >
          { this.renderPopoverContent() }
        </Popover>
        <Input {...InputProps} errorMessage={errorMessage}>
          { ({ id }) => (
            <div className={cx}>
              <button
                type="button"
                id={id}
                tabIndex="-1"
                className={classes.button}
                onClick={this.handleButtonClick}
              >
                <Typography
                  className={classes.buttonLabel}
                  color={date ? 'default' : 'textSecondary'}
                >
                  { date
                    ? momentOffset(utcOffset, date).format(format)
                    : placeholder
                  }
                </Typography>
              </button>
              { !required && !!date && (
                <IconButton className={classes.deleteButton} onClick={this.handleDelete}>
                  <CloseIcon className={classes.deleteButtonIcon} />
                </IconButton>
              ) }
            </div>
          ) }
        </Input>
      </Fragment>
    )
  }
}

export default class TimePickerValidator extends PureComponent { // eslint-disable-line
  static propTypes = {
    validation: PropTypes.object,
    required: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    minDateErrorMessage: PropTypes.any,
    innerRef: PropTypes.func,
  }

  static defaultProps = {
    innerRef: () => {},
  }

  validate = () => {
    const { required, minDate, minDateErrorMessage } = this.props
    const { date } = this.timePicker.state
    if (required) validateRequiredDate(date, 'Required field')
    if (minDate) validateMinDate(date, minDate, minDateErrorMessage)
  }

  innerRef = (component) => {
    const { innerRef } = this.props
    this.timePicker = component
    innerRef(component)
  }

  render() {
    const { validation, minDate, minDateErrorMessage, innerRef, ...rest } = this.props
    return (
      <Validator rule={this.validate}>
        { innerValidation => (
          <TimePicker
            {...rest}
            validation={validation || innerValidation}
            innerRef={this.innerRef}
          />
        ) }
      </Validator>
    )
  }
}
